
import { Habit, CategoryDefinition } from "@/types/habit";
import { HabitCard } from "./habit/HabitCard";

interface HabitListProps {
  habits: Habit[];
  selectedCategory: string | null;
  handleComplete: (id: string) => void;
  handleRevert: (id: string, entryIndex: number) => void;
  handleDelete: (id: string) => void;
  handleUpdateCategories: (id: string, categories: string[]) => void;
  handleUpdatePriority: (id: string, priority: "high" | "medium" | "low") => void;
  handleDuplicate: (id: string) => void;
  handleUpdateTitle: (id: string, title: string) => void;
  handleUpdateDailyTarget: (
    id: string,
    dailyTarget: { value: number; unit: string } | undefined
  ) => void;
  getAllCategories: () => CategoryDefinition[];
  handleAddNewCategoryToSystem: (category: string, iconSrc?: string) => void;
  showCompleted: boolean;
}

export const HabitList = ({
  habits,
  selectedCategory,
  handleComplete,
  handleRevert,
  handleDelete,
  handleUpdateCategories,
  handleUpdatePriority,
  handleDuplicate,
  handleUpdateTitle,
  handleUpdateDailyTarget,
  getAllCategories,
  handleAddNewCategoryToSystem,
  showCompleted,
}: HabitListProps) => {
  // Filter habits based on selected category and completion status
  const filteredHabits = habits.filter((habit) => {
    const matchesCategory =
      !selectedCategory ||
      habit.category.includes(selectedCategory);
    const matchesCompletion =
      showCompleted
        ? habit.completedDays === habit.totalDays
        : habit.completedDays < habit.totalDays;
    return matchesCategory && matchesCompletion;
  });

  return (
    <div className="space-y-6 w-full px-4 sm:px-6">
      {filteredHabits.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredHabits.map((habit) => (
            <HabitCard
              key={habit.id}
              habit={habit}
              onComplete={handleComplete}
              onRevert={(id) => handleRevert(id, 0)} 
              onDelete={handleDelete}
              onUpdateCategories={handleUpdateCategories}
              onUpdatePriority={handleUpdatePriority}
              onDuplicate={handleDuplicate}
              onUpdateTitle={handleUpdateTitle}
              onUpdateDailyTarget={(id, value, unit) => {
                handleUpdateDailyTarget(id, { value, unit });
              }}
              availableCategories={getAllCategories().map(cat => cat.name)}
              handleAddNewCategoryToSystem={handleAddNewCategoryToSystem}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500">
            {showCompleted
              ? "No finished habits yet."
              : selectedCategory
              ? `No active habits in "${selectedCategory}".`
              : "No active habits yet."}
          </p>
        </div>
      )}
    </div>
  );
};
